import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  AppIcon,
  DotBadge,
  GenericTable,
  HorizontalMenuTabs,
  InfoModal,
} from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { CoachingGuideButtons } from '@backrs_ui/grow-ui/src/components/CoachingGuideButtons/CoachingGuideButtons';
import { NoticeHost } from '@backrs_ui/grow-ui/src/components/InstitutionWrite/NoticeHost';
import { Header } from '@backrs_ui/grow-ui/src/components/Lubav/Header';
import { OperatorContextSwitcher } from '@backrs_ui/grow-ui/src/components/PageShell/OperatorContextSwitcher';
import {
  type RowAction,
  RowActionMenu,
} from '@backrs_ui/grow-ui/src/components/RowActionMenu/RowActionMenu';
import { SandboxContactButton } from '@backrs_ui/grow-ui/src/components/SandboxContactButton/SandboxContactButton';
import { useEmulateUser } from '@backrs_ui/grow-ui/src/hooks/useEmulateUser';
import { usePageTitle } from '@backrs_ui/grow-ui/src/hooks/usePageTitle';
import { useSandboxGate } from '@backrs_ui/grow-ui/src/hooks/useSandboxGate';
import { openGettingStarted } from '@backrs_ui/grow-ui/src/redux/appState/appState';
import type {
  Assignee,
  CoachingDashboardSortField,
  CoachSummary,
  InstitutionMember,
} from '@backrs_ui/grow-ui/src/redux/coachingDashboard';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import { SEAT_KIND, type SeatKind } from '@backrs_ui/grow-ui/src/redux/institutionAdmin';
import { selectIsPlatformContext } from '@backrs_ui/grow-ui/src/redux/operatorContext';
import { useFetchUserPermissionsQuery } from '@backrs_ui/grow-ui/src/redux/user/user.api';
import { UI_PERMISSIONS } from '@backrs_ui/grow-ui/src/redux/user/user.transformer';
import { personInitial, personName } from '@backrs_ui/grow-ui/src/utils/personName';
import { IS_SANDBOX } from '@backrs_ui/grow-ui/src/utils/sandboxSession';

import CheckIcon from '@mui/icons-material/Check';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import RemoveIcon from '@mui/icons-material/Remove';
import {
  Alert,
  Avatar,
  AvatarGroup,
  Box,
  Button,
  Checkbox,
  Chip,
  Snackbar,
  type SxProps,
  TableCell,
  TablePagination,
  TableRow,
  TableSortLabel,
  type Theme,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';

import { BulkAssignCoachesModal } from './BulkAssignCoachesModal';
import { CoachingDashboardFilterBar } from './CoachingDashboardFilterBar';
import { useAgentContext } from './CoachingDashboardPage.agentContext';
import {
  coachTooltipLabel,
  orderCoaches,
  ROSTER_COLUMNS,
  ROSTER_TAB,
  ROSTER_TABS,
  type RosterTab,
} from './coachingDashboardTabs';
import { showsFiltersUnavailable } from './dashboardVisibility';
import { InScopeInstitutionFilter } from './InScopeInstitutionFilter';
import { MemberAssessmentsCell, MemberAssessmentsHeaderInfo } from './MemberAssessmentsCell';
import { OperatorRootPicker } from './OperatorRootPicker';
import { ReportsModal } from './ReportsModal';
import {
  seatedInstitutions,
  seatedPrograms,
  type SeatedInstitution,
  type SeatHolder,
} from './seatActions/memberSeats';
import {
  bulkSeatActions,
  coachRowActions,
  staffRowActions,
  studentRowActions,
} from './seatActions/rowActions';
import { SeatActionDialogs } from './seatActions/SeatActionDialogs';
import { useSeatActions } from './seatActions/useSeatActions';
import { useCoachingDashboardController } from './useCoachingDashboardController';

const {
  modules: { coachingDashboard, pageShell },
} = appStrings;

type Member = Assignee | InstitutionMember;

const ROWS_PER_PAGE_OPTIONS = [50, 75, 100];

const TAB_LABELS: Record<RosterTab, string> = {
  [ROSTER_TAB.STUDENTS]: coachingDashboard.studentsTabTitle,
  [ROSTER_TAB.COACHES]: coachingDashboard.coachesTabTitle,
  [ROSTER_TAB.STAFF]: coachingDashboard.staffTabTitle,
};

const TAB_SEAT_DOORS: Record<
  RosterTab,
  Readonly<{ rowActions: typeof studentRowActions; seatKind: SeatKind }>
> = {
  [ROSTER_TAB.STUDENTS]: { rowActions: studentRowActions, seatKind: SEAT_KIND.student },
  [ROSTER_TAB.COACHES]: { rowActions: coachRowActions, seatKind: SEAT_KIND.coach },
  [ROSTER_TAB.STAFF]: { rowActions: staffRowActions, seatKind: SEAT_KIND.staff },
};

const formatLastLogin = (value?: string | null): string =>
  value ? new Date(value).toLocaleDateString() : '';

const seatHolder = (member: Member): SeatHolder => ({
  id: member.id,
  name: personName(member),
  seats: member.seats,
  cohorts: member.cohorts,
});

export const CoachingDashboardPage = () => (
  <NoticeHost id="coaching-dashboard-notice">
    <CoachingDashboardPageContent />
  </NoticeHost>
);

const CoachingDashboardPageContent = () => {
  const {
    members,
    totalCount,
    page,
    rowsPerPage,
    isLoading,
    isRosterUnavailable,
    isCoach,
    isInstitutionAdmin,
    isPeopleTab,
    showStudentFilters,
    showInstitutionFilters,
    showInstitutionRoleFilter,
    searchValue,
    onSearchChange,
    onPageChange,
    onRowsPerPageChange,
    filterOptions,
    filters,
    dispatchFilters,
    filterRoles,
    cohortOptions,
    showCohortFilter,
    coachOptions,
    showCoachFilter,
    sort,
    onToggleSort,
    activeTab,
    onTabChange,
    inScopeInstitutionId,
    onInScopeInstitutionChange,
    inScopeInstitutions,
    filtersError,
    heldRoots,
    onOperatorInstitutionSelect,
  } = useCoachingDashboardController();

  const isFiltersUnavailable = showsFiltersUnavailable(Boolean(filtersError), {
    hasUnresolvedContext: heldRoots !== null,
  });
  const showsInScopeInstitutions =
    !isCoach && heldRoots === null && (inScopeInstitutions.length > 0 || isFiltersUnavailable);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isPlatformMode = useAppSelector(selectIsPlatformContext);
  const gateInSandbox = useSandboxGate();
  const { backrs } = useTheme();

  const { data: userPermissions } = useFetchUserPermissionsQuery(undefined);
  const canEmulate =
    (userPermissions?.[UI_PERMISSIONS.EMULATE_USERS]?.allowed ?? false) && !isPlatformMode;

  const [selectedMembers, setSelectedMembers] = useState<Map<number, SeatHolder>>(new Map());
  const seatActions = useSeatActions();
  const assignCoachesMembers = useMemo(
    () => new Map((seatActions.action?.holders ?? []).map((holder) => [holder.id, holder])),
    [seatActions.action],
  );
  const [isReportsOpen, setIsReportsOpen] = useState(false);
  const [showCopied, setShowCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent, value: string | null) => {
    e.stopPropagation();
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
      setShowCopied(true);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  const clearSelection = () => setSelectedMembers(new Map());

  const clearWhatLanded = () => {
    const acted = seatActions.action?.holders ?? [];

    if (acted.some((holder) => selectedMembers.has(holder.id))) clearSelection();
  };

  const visibleIds = members.map((m) => m.id);
  const selectedOnPageCount = visibleIds.filter((id) => selectedMembers.has(id)).length;
  const allOnPageSelected = visibleIds.length > 0 && selectedOnPageCount === visibleIds.length;
  const someOnPageSelected = selectedOnPageCount > 0 && !allOnPageSelected;

  const toggleSelectAllOnPage = () => {
    setSelectedMembers((prev) => {
      const next = new Map(prev);

      if (allOnPageSelected) {
        members.forEach((m) => next.delete(m.id));
      } else {
        members.forEach((m) => next.set(m.id, seatHolder(m)));
      }

      return next;
    });
  };

  const toggleMemberSelected = (member: Member) => {
    setSelectedMembers((prev) => {
      const next = new Map(prev);

      if (next.has(member.id)) next.delete(member.id);
      else next.set(member.id, seatHolder(member));

      return next;
    });
  };

  const pageTitle =
    isInstitutionAdmin && !isCoach ? coachingDashboard.adminMainTitle : coachingDashboard.mainTitle;

  usePageTitle(pageTitle);
  useAgentContext({
    activeTab: isInstitutionAdmin ? activeTab : null,
    tabLabel: isInstitutionAdmin ? TAB_LABELS[activeTab] : null,
    members,
    totalCount,
    searchValue,
  });
  const {
    isRequestingToken,
    isDialogOpen,
    error: emulationError,
    selectedMember,
    openEmulationDialog,
    handleCloseDialog,
    handleConfirmEmulation,
    handleCloseError,
  } = useEmulateUser<Member>();

  const location = useLocation();
  const handleRowClick = (memberId: number) => {
    const memberPath = APP_ROUTE.COACHING_DASHBOARD_MEMBER.replace(':memberId', String(memberId));

    navigate(memberPath, {
      state: { from: `${location.pathname}${location.search}` },
    });
  };

  const sortHeader = (field: CoachingDashboardSortField, text: string) => {
    const active = sort?.field === field;

    return (
      <TableSortLabel
        id={`coaching-dashboard-${field}-sort`}
        active={active}
        direction={active ? (sort?.direction === 'ASC' ? 'asc' : 'desc') : 'asc'}
        onClick={() => onToggleSort(field)}
      >
        {text}
      </TableSortLabel>
    );
  };

  const tabInstitutions = (member: Member): readonly SeatedInstitution[] => {
    // A row read without its seats still names its institutions, of every seat kind.
    if (!isPeopleTab || !member.seats.known) return member.institutions ?? [];

    return seatedInstitutions(member.seats, TAB_SEAT_DOORS[activeTab].seatKind);
  };

  const renderInstitutionCell = (member: Member) => {
    const institutions = tabInstitutions(member);
    const visibleCount = isPeopleTab ? COACH_VISIBLE_INSTITUTIONS : 1;
    const shown = institutions.slice(0, visibleCount);
    const rest = institutions.slice(visibleCount);

    const cellSx = isPeopleTab ? coachInstitutionCellSx : undefined;

    if (shown.length === 0) {
      return <TableCell sx={cellSx}>{coachingDashboard.noInstitution}</TableCell>;
    }

    return (
      <TableCell sx={cellSx}>
        <Box sx={institutionCellSx}>
          {shown.map((institution) => (
            <Chip
              key={institution.id}
              label={institution.name}
              variant="tag"
              size="sm"
              sx={chipSx}
            />
          ))}
          {rest.length > 0 ? (
            <Tooltip title={rest.map((institution) => institution.name).join(', ')} arrow>
              <Box component="span">
                <Chip label={`+${rest.length}`} variant="tag" size="sm" sx={chipSx} />
              </Box>
            </Tooltip>
          ) : null}
        </Box>
      </TableCell>
    );
  };

  // Every write here resolves one operator, and platform mode resolves none.
  const canWrite = isInstitutionAdmin && !isPlatformMode;
  const canSelectMembers = canWrite && !isPeopleTab;
  // Which seat doors the menu carries is the tab's call.
  const hasRowActions = canWrite;
  const hasRowMenu = hasRowActions || canEmulate;

  const rowActionsFor = (member: Member) => {
    const holder = seatHolder(member);
    const { rowActions, seatKind } = TAB_SEAT_DOORS[activeTab];

    return rowActions(holder, (kind) => {
      if (gateInSandbox()) return;

      seatActions.open(kind, [holder], seatKind);
    });
  };

  const emulateAction = (member: Member): RowAction => ({
    id: `coaching-dashboard-emulate-${member.id}`,
    label: coachingDashboard.emulateLabel,
    onSelect: () => openEmulationDialog(member),
  });

  const selectAllHeader = canSelectMembers ? (
    <Checkbox
      id="coaching-dashboard-select-all-checkbox"
      checked={allOnPageSelected}
      indeterminate={someOnPageSelected}
      onChange={toggleSelectAllOnPage}
      icon={<CheckBoxOutlineBlankIcon />}
      checkedIcon={<CheckIcon />}
      indeterminateIcon={<RemoveIcon />}
      inputProps={{ 'aria-label': 'Select all members on this page' }}
    />
  ) : null;

  const studentColumns = ROSTER_COLUMNS[ROSTER_TAB.STUDENTS];
  const peopleColumns: Readonly<Record<string, string>> = ROSTER_COLUMNS[activeTab];
  const peopleHeader = (key: string, label: string) => {
    if (key === 'name') return sortHeader('last_name', label);
    if (key === 'studentCount') return sortHeader('student_count', label);

    return label;
  };

  const baseHeaders = isPeopleTab
    ? Object.entries(peopleColumns).map(([key, label]) => peopleHeader(key, label))
    : [
        ...(canSelectMembers ? [selectAllHeader] : []),
        sortHeader('last_name', studentColumns.name),
        studentColumns.coach,
        studentColumns.program,
        sortHeader('grade', studentColumns.grade),
        studentColumns.school,
        studentColumns.institution,
        <Box key="assessments" sx={assessmentsHeaderSx}>
          {studentColumns.assessments}
          <MemberAssessmentsHeaderInfo />
        </Box>,
        sortHeader('last_login', studentColumns.lastLogin),
      ];
  const headers = hasRowMenu ? [...baseHeaders, ''] : baseHeaders;

  const inScopeInstitutionField = isFiltersUnavailable ? (
    <Typography variant="text-sm-regular" sx={rosterNoticeSx} role="status">
      {coachingDashboard.institutionAdmin.filtersUnavailable}
    </Typography>
  ) : (
    <InScopeInstitutionFilter
      institutions={inScopeInstitutions}
      selectedInstitutionId={inScopeInstitutionId}
      onChange={onInScopeInstitutionChange}
    />
  );

  return (
    <Box sx={containerSx}>
      <Header
        title={pageTitle}
        stickerUrl={null}
        newChatLabel=""
        sx={headerSx}
        actionButtons={
          <>
            <CoachingGuideButtons idPrefix="coaching-dashboard" />
            {isInstitutionAdmin && (
              <Button
                id="coaching-dashboard-reports-button"
                hierarchy="Secondary gray"
                size="md"
                startIcon={<AppIcon iconName="Download01" diameter="20px" color="currentColor" />}
                onClick={() => setIsReportsOpen(true)}
              >
                {coachingDashboard.reports.title}
              </Button>
            )}
            {IS_SANDBOX && (
              <>
                <Button
                  id="coaching-dashboard-getting-started-button"
                  hierarchy="Secondary color"
                  size="md"
                  sx={noWrapSx}
                  onClick={() => dispatch(openGettingStarted())}
                >
                  {pageShell.header.gettingStartedButton}
                </Button>
                <SandboxContactButton />
              </>
            )}
          </>
        }
      />
      <Box sx={contentSx}>
        <OperatorContextSwitcher id="coaching-dashboard-operator-context" />
        {isInstitutionAdmin && heldRoots !== null ? (
          <OperatorRootPicker heldRoots={heldRoots} onSelect={onOperatorInstitutionSelect} />
        ) : (
          <>
            {isInstitutionAdmin ? (
              <HorizontalMenuTabs
                id="coaching-dashboard-roster-tabs"
                variant="box"
                containerSxOverride={rosterTabsSx}
                tabs={ROSTER_TABS.map((tab) => TAB_LABELS[tab])}
                value={ROSTER_TABS.indexOf(activeTab)}
                setValue={(index) => {
                  clearSelection();
                  onTabChange(ROSTER_TABS[index] ?? ROSTER_TAB.STUDENTS);
                }}
              />
            ) : null}
            <CoachingDashboardFilterBar
              searchValue={searchValue}
              searchPlaceholder={coachingDashboard.searchPlaceholder}
              onSearchChange={onSearchChange}
              filters={filters}
              filterOptions={filterOptions}
              dispatch={dispatchFilters}
              showInstitutionFilters={showInstitutionFilters}
              showStudentFilters={showStudentFilters}
              showInstitutionRoleFilter={showInstitutionRoleFilter}
              roles={filterRoles}
              cohorts={cohortOptions}
              showCohortFilter={showCohortFilter}
              coaches={coachOptions}
              showCoachFilter={showCoachFilter}
              institutionFilter={showsInScopeInstitutions ? inScopeInstitutionField : null}
              isInstitutionSelected={inScopeInstitutionId !== null}
              selectionCount={canSelectMembers ? selectedMembers.size : 0}
              onClearSelection={clearSelection}
              bulkActions={
                canWrite
                  ? bulkSeatActions(Array.from(selectedMembers.values()), (kind) => {
                      if (gateInSandbox()) return;

                      seatActions.open(
                        kind,
                        Array.from(selectedMembers.values()),
                        SEAT_KIND.student,
                      );
                    })
                  : []
              }
            />
            {isRosterUnavailable ? (
              <Typography variant="text-sm-regular" sx={rosterNoticeSx}>
                {coachingDashboard.loadError}
              </Typography>
            ) : null}
            {isLoading && members.length === 0 ? (
              <Typography variant="text-sm-regular" sx={rosterNoticeSx}>
                {coachingDashboard.loadingRoster}
              </Typography>
            ) : null}
            <GenericTable headers={headers} ariaLabel="Members table" stickyHeader>
              {members.map((member) => {
                const programs = member.seats.known
                  ? seatedPrograms(member.seats.seats, SEAT_KIND.student)
                  : null;

                return (
                  <TableRow
                    key={member.id}
                    id={`coaching-dashboard-member-row-${member.id}`}
                    onClick={isPeopleTab ? undefined : () => handleRowClick(member.id)}
                    sx={isPeopleTab ? staticRowSx : tableRowSx}
                  >
                    {canSelectMembers && (
                      <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          id={`coaching-dashboard-select-${member.id}`}
                          checked={selectedMembers.has(member.id)}
                          onChange={() => toggleMemberSelected(member)}
                          icon={<CheckBoxOutlineBlankIcon />}
                          checkedIcon={<CheckIcon />}
                          inputProps={{
                            'aria-label': `Select ${personName(member)}`,
                          }}
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      <Box sx={memberNameCellSx}>
                        <Box sx={memberAvatarWrapperSx}>
                          <Avatar
                            src={member.profile_image ?? undefined}
                            alt={personName(member)}
                            sx={memberAvatarSx}
                          >
                            {personInitial(member)}
                          </Avatar>
                          {member.is_verified ? (
                            <Tooltip title={coachingDashboard.verifiedBadge} arrow>
                              <Box
                                id={`coaching-dashboard-verified-${member.id}`}
                                sx={verifiedMarkSx}
                                role="img"
                                aria-label={coachingDashboard.verifiedBadge}
                              >
                                <AppIcon
                                  iconName="Check"
                                  diameter="8px"
                                  color="Colors/Base/White"
                                />
                              </Box>
                            </Tooltip>
                          ) : null}
                        </Box>
                        <Box>
                          <Typography variant="text-sm-medium">{personName(member)}</Typography>
                          {member.email ? (
                            <Typography
                              id={`coaching-dashboard-copy-email-${member.id}`}
                              component="span"
                              variant="text-sm-regular"
                              sx={copyableTextSx}
                              onClick={(e) => handleCopy(e, member.email)}
                              role="button"
                              aria-label={`Copy email ${member.email}`}
                            >
                              {member.email}
                            </Typography>
                          ) : null}
                        </Box>
                      </Box>
                    </TableCell>
                    {isPeopleTab ? (
                      <>
                        {'institution' in peopleColumns ? renderInstitutionCell(member) : null}
                        {'studentCount' in peopleColumns ? (
                          <TableCell>
                            <Typography variant="text-sm-regular">
                              {member.student_count ?? 0}
                            </Typography>
                          </TableCell>
                        ) : null}
                      </>
                    ) : (
                      <>
                        <TableCell sx={coachesCellSx}>
                          <Tooltip
                            title={coachesTooltipContent(member.coaches)}
                            placement="top"
                            arrow
                            disableHoverListener={member.coaches.length === 0}
                            slotProps={{
                              tooltip: { sx: tooltipSx },
                              arrow: { sx: tooltipArrowSx },
                            }}
                          >
                            <AvatarGroup max={5} total={member.coaches.length} sx={avatarGroupSx}>
                              {orderCoaches(member.coaches).map((coach) => (
                                <Box key={coach.id} sx={coachAvatarWrapperSx}>
                                  <Avatar
                                    src={coach.profile_image_url ?? undefined}
                                    alt={personName(coach)}
                                  >
                                    {personInitial(coach)}
                                  </Avatar>
                                  {coach.is_backrs_coach ? (
                                    <Box sx={backrsCoachMarkSx}>
                                      <DotBadge
                                        ariaLabel={coachingDashboard.backrsCoach}
                                        dotColor={
                                          backrs.colors['Colors/Foreground/fg-brand-primary']
                                        }
                                      />
                                    </Box>
                                  ) : null}
                                </Box>
                              ))}
                            </AvatarGroup>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          {programs === null ? (
                            <Typography variant="text-sm-regular" sx={gradeSx}>
                              {coachingDashboard.programUnknown}
                            </Typography>
                          ) : (
                            programs.map((program) => (
                              <Chip
                                key={program}
                                label={program}
                                variant="tag"
                                size="sm"
                                sx={chipSx}
                              />
                            ))
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography sx={gradeSx}>{member.grade}</Typography>
                        </TableCell>
                        <TableCell>
                          {member.school ? (
                            <Tooltip title={member.school} placement="top" arrow>
                              <Chip
                                label={member.school}
                                variant="tag"
                                size="sm"
                                sx={[chipSx, clippedChipSx] as SxProps<Theme>}
                              />
                            </Tooltip>
                          ) : null}
                        </TableCell>
                        {renderInstitutionCell(member)}
                        <TableCell>
                          <MemberAssessmentsCell assessments={member.assessments} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="text-sm-regular">
                            {formatLastLogin(member.last_login)}
                          </Typography>
                        </TableCell>
                      </>
                    )}
                    {hasRowMenu && (
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <RowActionMenu
                          id={`coaching-dashboard-row-actions-${member.id}`}
                          ariaLabel={coachingDashboard.rowMenuAria(personName(member))}
                          actions={[
                            ...(hasRowActions ? rowActionsFor(member) : []),
                            ...(canEmulate ? [emulateAction(member)] : []),
                          ]}
                        />
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </GenericTable>
            <TablePagination
              id="coaching-dashboard-pagination"
              component="div"
              count={totalCount}
              page={page}
              onPageChange={onPageChange}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={onRowsPerPageChange}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              disabled={isLoading}
              sx={paginationSx}
            />
          </>
        )}
      </Box>
      <BulkAssignCoachesModal
        open={seatActions.action?.kind === 'assignCoaches'}
        onClose={seatActions.close}
        membersById={assignCoachesMembers}
        onSuccess={clearWhatLanded}
      />
      <SeatActionDialogs
        action={seatActions.action}
        onClose={seatActions.close}
        onLanded={clearWhatLanded}
      />
      <ReportsModal open={isReportsOpen} onClose={() => setIsReportsOpen(false)} />
      <InfoModal
        open={isDialogOpen}
        onClose={handleCloseDialog}
        width="sm"
        featuredIcon={{
          iconName: 'Users01',
          size: 'lg',
          color: 'Colors/Foreground/fg-brand-primary',
        }}
        headerContent={<Typography variant="h6">Emulate User</Typography>}
        bodyContent={
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {selectedMember === null ? null : (
              <Typography>
                {coachingDashboard.emulateConfirmation(personName(selectedMember))}
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                id="coaching-dashboard-emulate-cancel-button"
                hierarchy="Secondary gray"
                size="md"
                onClick={handleCloseDialog}
              >
                Cancel
              </Button>
              <Button
                id="coaching-dashboard-emulate-confirm-button"
                hierarchy="Primary"
                size="md"
                onClick={handleConfirmEmulation}
                disabled={isRequestingToken}
              >
                {isRequestingToken ? 'Starting...' : 'Start Emulation'}
              </Button>
            </Box>
          </Box>
        }
      />
      <Snackbar
        open={!!emulationError}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {emulationError}
        </Alert>
      </Snackbar>
      <Snackbar
        open={showCopied}
        autoHideDuration={2000}
        onClose={() => setShowCopied(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowCopied(false)} severity="success" sx={{ width: '100%' }}>
          {coachingDashboard.copiedToClipboard}
        </Alert>
      </Snackbar>
    </Box>
  );
};

const containerSx: SxProps<Theme> = {
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  minHeight: 0,
};

const headerSx: SxProps<Theme> = ({ backrs }) => ({
  paddingLeft: backrs.spacing['spacing-4xl'],
  paddingRight: { xs: backrs.spacing['spacing-4xl'], sm: backrs.spacing['spacing-4xl'] },
  paddingTop: { xs: backrs.spacing['spacing-4xl'], sm: backrs.spacing['spacing-4xl'] },
});

const contentSx: SxProps<Theme> = ({ backrs }) => ({
  containerType: 'inline-size',
  paddingLeft: backrs.spacing['spacing-4xl'],
  paddingRight: backrs.spacing['spacing-4xl'],
  paddingTop: backrs.spacing['spacing-xl'],
  paddingBottom: backrs.spacing['spacing-xl'],
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
});

// The roster overflows this column's height, and a flex item shrinks by
// default, so the tab strip needs holding at its own height.
const rosterTabsSx: SxProps<Theme> = ({ backrs }) => ({
  flexShrink: 0,
  alignSelf: 'flex-start',
  marginBottom: backrs.spacing['spacing-3xl'],
});

const staticRowSx: SxProps<Theme> = ({ backrs }) => ({
  '& .MuiTableCell-root': {
    paddingBlock: backrs.spacing['spacing-xl'],
  },
});

const tableRowSx: SxProps<Theme> = ({ backrs }) => ({
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: backrs.colors['Colors/Background/bg-secondary-hover'],
  },
  '& .MuiTableCell-root': {
    paddingBlock: backrs.spacing['spacing-xl'],
  },
});

/** Five 24px avatars overlapped by 8px, plus the overflow count. */
const COACHES_COLUMN_WIDTH = '108px';

const coachesCellSx: SxProps<Theme> = {
  width: COACHES_COLUMN_WIDTH,
  '& .MuiAvatarGroup-root': {
    justifyContent: 'flex-start',
    width: 'fit-content',
  },
};

const clippedChipSx: SxProps<Theme> = {
  maxWidth: COACHES_COLUMN_WIDTH,
  '& .MuiChip-label': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
};

const memberNameCellSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-lg'],
});

const memberAvatarSx: SxProps<Theme> = ({ backrs }) => ({
  width: 40,
  height: 40,
  fontSize: '0.875rem',
  backgroundColor: backrs.colors['Colors/Background/bg-tertiary'],
  color: backrs.colors['Colors/Foreground/fg-primary'],
  border: `1px solid ${backrs.colors['Gray/600']}`,
});

const memberAvatarWrapperSx: SxProps<Theme> = {
  position: 'relative',
  display: 'inline-flex',
  flexShrink: 0,
};

const verifiedMarkSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'absolute',
  right: '-2px',
  bottom: '-2px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: backrs.spacing['spacing-xxs'],
  backgroundColor: backrs.colors['Colors/Foreground/fg-success-secondary'],
  border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  borderRadius: backrs.radius['radius-full'],
});

const coachAvatarWrapperSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'relative',
  display: 'inline-flex',
  borderRadius: backrs.radius['radius-full'],
  lineHeight: 0,
});

const backrsCoachMarkSx: SxProps<Theme> = {
  position: 'absolute',
  right: '-2px',
  bottom: '-2px',
  pointerEvents: 'none',
};

// AvatarGroup hangs its overlap margin off `.MuiAvatar-root`, whose
// `:last-child` rule resolves within a wrapper rather than across the group.
// Overlap the wrappers instead; the group marks its own direct children, so the
// `+N` circle still carries it.
const avatarGroupSx: SxProps<Theme> = ({ backrs, typography }) => ({
  '& > .MuiAvatarGroup-avatar': {
    marginLeft: '-8px',
    '&:last-child': { marginLeft: 0 },
  },
  '& > .MuiAvatarGroup-avatar > .MuiAvatar-root': { marginLeft: 0 },
  '& .MuiAvatar-root': {
    width: 24,
    height: 24,
    ...typography['text-xs-semibold'],
    backgroundColor: backrs.colors['Colors/Background/bg-tertiary'],
    color: backrs.colors['Colors/Text/text-quaternary'],
  },
});

const noWrapSx: SxProps<Theme> = { whiteSpace: 'nowrap' };

const tooltipSx: SxProps<Theme> = ({ backrs }) => ({
  backgroundColor: backrs.colors['Colors/Background/bg-primary-solid'],
  padding: backrs.spacing['spacing-md'],
  borderRadius: backrs.radius['radius-md'],
});

const tooltipArrowSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Background/bg-primary-solid'],
});

const paginationSx: SxProps<Theme> = ({ backrs }) => ({
  flexShrink: 0,
  '& .MuiTablePagination-toolbar': {
    paddingLeft: backrs.spacing['spacing-3xl'],
    paddingRight: backrs.spacing['spacing-xl'],
  },
  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
    color: backrs.colors['Colors/Text/text-quaternary'],
  },
});

const tooltipAvatarSx: SxProps<Theme> = ({ backrs }) => ({
  width: 24,
  height: 24,
  fontSize: '0.75rem',
  backgroundColor: backrs.colors['Colors/Background/bg-tertiary'],
  color: backrs.colors['Colors/Foreground/fg-primary'],
});

const gradeSx: SxProps<Theme> = ({ backrs, typography }) => ({
  ...typography['text-sm-regular'],
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const copyableTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  cursor: 'pointer',
  width: 'fit-content',
  '&:hover': {
    textDecoration: 'underline',
  },
});

const chipSx: SxProps<Theme> = ({ backrs, typography }) => ({
  width: 'fit-content',
  ...typography['text-xs-medium'],
  color: backrs.colors['Colors/Text/text-secondary'],
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  textTransform: 'capitalize',
  border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
});

const rosterNoticeSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
  py: backrs.spacing['spacing-md'],
});

// The coaches table has few columns, so Institution takes the slack instead of the name column.
const COACH_VISIBLE_INSTITUTIONS = 3;

const coachInstitutionCellSx: SxProps<Theme> = {
  width: '40%',
};

const institutionCellSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xs'],
});

const assessmentsHeaderSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xs'],
});

const coachesTooltipContent = (coaches: readonly CoachSummary[]) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
    {orderCoaches(coaches).map((coach) => (
      <Box key={coach.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Avatar
          src={coach.profile_image_url ?? undefined}
          alt={personName(coach)}
          sx={tooltipAvatarSx}
        >
          {personInitial(coach)}
        </Avatar>
        <Typography variant="text-xs-regular" sx={{ color: 'inherit' }}>
          {coachTooltipLabel(coach, coachingDashboard.backrsCoach)}
        </Typography>
      </Box>
    ))}
  </Box>
);
